import type { TodoDto, VorgangDto } from "../../../services/vorgangService";

/**
 * Was die LISTE nach einem Verschmelzen zeigen muss (PO 21.09.2026: „das
 * verschmelzen muss in echtzeit angezeigt werden, die veränderung nicht erst
 * durch reload").
 *
 * ══ WARUM NICHT EINFACH NEU LADEN ══════════════════════════════════════════
 * Ein Neuladen des Feeds wirft alle nachgeladenen Seiten weg und lässt nur
 * die erste stehen. Wer weit unten arbeitet, sieht danach etwas ganz anderes
 * — das Ergebnis der eigenen Handlung findet er gar nicht. Dieselbe Erfahrung
 * hat die Tabelle schon beim Termin-Speichern gemacht („kein Refetch, sonst
 * springt die Scroll-Position"); hier gilt sie genauso.
 *
 * Also: Die Quellzeile verschwindet lokal, und die Zielzeile bekommt die
 * wenigen Felder frisch, die sich tatsächlich geändert haben.
 *
 * ══ DIE REGEL: NUR ZEIGEN, WAS EIN NEULADEN AUCH ZEIGTE ════════════════════
 * Das ist der Maßstab für alles hier. Eine lokale Aktualisierung, die MEHR
 * weiß als der Feed, ist keine Echtzeit, sondern ein Flackern: Sie zeigt für
 * ein paar Minuten etwas anderes als die Liste nach dem nächsten Laden.
 *
 * Deshalb wird die Zielzeile NICHT ersetzt, sondern ergänzt — und die
 * ergänzten Felder werden auf die Form des Feeds zurückgebracht.
 * `GET /vorgang/:id` ist ein anderer Endpunkt und reichert in BEIDE
 * Richtungen anders an:
 *  - Er lädt Relationen nicht, die nur der Feed auflöst → ein vollständiges
 *    Überschreiben setzte sie still auf `undefined`, und die Zeile verlöre
 *    Spalten, obwohl nichts kaputt ist.
 *  - Er lädt Relationen, die der Feed NICHT hat → siehe `wieImFeed`.
 *
 * Verschmelzen ändert am Ziel genau dies:
 *  - `todos` — die To-dos der Quelle kommen hinzu, und die Invariante („mit
 *    To-dos gehört alles einem To-do") kann ein weiteres anlegen;
 *  - den Fortschritt, den der Server danach neu zählt;
 *  - `statusManuell`/`status` — ein von Hand gesetzter Status wirkt nur ohne
 *    To-dos; hat das Ziel plötzlich welche, verliert er seine Wirkung.
 */

/** Die Felder, die ein Verschmelzen am Ziel verändern kann. */
export interface VerschmelzFrisch {
    todos?: TodoDto[];
    fortschrittErledigt?: number;
    fortschrittGesamt?: number;
    statusManuell?: string | null;
    status?: string;
}

/**
 * Ein Kind-To-do auf die Form bringen, die der FEED liefert.
 *
 * `findOne` setzt auf JEDEM Kind-To-do eine `kunde`-Zusammenfassung und fällt
 * dabei bis auf den Kunden des Vorgangs zurück. Der Feed tut das nicht — er
 * hängt `kunde` nur an eigenständige To-dos, und die liefert er längst nicht
 * mehr. In der Liste sind die Kontakt-Zellen aufgeklappter To-do-Zeilen also
 * leer, und zwar immer.
 *
 * Bliebe das Feld stehen, träte genau das Flackern ein, das die Regel oben
 * verbietet: Nach dem Verschmelzen zeigten DIESELBEN, unveränderten To-dos
 * plötzlich einen Kontaktnamen und ein FO/BO-Badge — und beim nächsten Laden
 * wären sie wieder leer. Schlimmer noch: `canEditTodo` prüft unter anderem die
 * Kontaktverantwortlichkeit, also erschiene für den FO/BO dieses Kunden ein
 * „Löschen" an Zeilen, an denen es vorher keins gab. Das Backend bliebe die
 * letzte Instanz — ein Rechteloch ist es nicht —, aber eine Schaltfläche, die
 * auftaucht und wieder verschwindet, ist schlimmer als keine.
 */
function wieImFeed(todos: TodoDto[]): TodoDto[] {
    return todos.map((t) => {
        if (t.kunde == null) return t;
        const { kunde: _weg, ...rest } = t;
        return rest as TodoDto;
    });
}

/**
 * Die alte Zeile mit dem frisch Geladenen ergänzen.
 *
 * Ohne frische Daten (der Nachschlag ist fehlgeschlagen) bleibt die alte
 * Zeile unverändert stehen. Das ist der ehrlichere Ausgang als ein geratener
 * Fortschritt: Die Quellzeile ist dann trotzdem fort — das Verschmelzen hat
 * ja stattgefunden —, nur die Zahlen am Ziel hinken bis zum nächsten Laden.
 */
export function zielNachVerschmelzen(
    alt: VorgangDto,
    frisch: VerschmelzFrisch | null | undefined,
): VorgangDto {
    if (!frisch) return alt;
    const neu: VorgangDto = { ...alt };
    // Feld für Feld, und nur wenn der Server es überhaupt mitgeschickt hat.
    // `statusManuell` darf legitim `null` sein — deshalb `in` statt
    // `!== undefined`: Ein zurückgenommener Handstatus ist eine echte
    // Änderung, die ankommen muss.
    if (Array.isArray(frisch.todos)) neu.todos = wieImFeed(frisch.todos);
    if (typeof frisch.fortschrittErledigt === "number") {
        neu.fortschrittErledigt = frisch.fortschrittErledigt;
    }
    if (typeof frisch.fortschrittGesamt === "number") {
        neu.fortschrittGesamt = frisch.fortschrittGesamt;
    }
    if ("statusManuell" in frisch) neu.statusManuell = frisch.statusManuell ?? null;
    if (typeof frisch.status === "string") neu.status = frisch.status;
    return neu;
}
